import Phaser from "phaser";
import { StartBtn } from "@phaser/ui/btns/StartBtn";
import { VolumeBtn } from "@phaser/ui/btns/VolumeBtn";
import { HelpBtn } from "@phaser/ui/btns/HelpBtn";
import { AudioManager } from "@/phaser/core/audio/AudioManager";
import { ModalManager, ModalType } from "@phaser/ui/modals/ModalManager";
import {
  ArrowDirection,
  ArrowRole,
  SArrowBtn,
} from "@phaser/ui/btns/SArrowBtn";

import { STAGES } from "@phaser/core/stages/StageCatalog";
import { BestPanel } from "@/phaser/ui/home/BestPanel";
import { BottomNavBar } from "@phaser/ui/navs/BottomNavBar";
import { shopEquipManager } from "../core/shop/ShopEquipManager";
import {
  applyFloatingEffect,
  PortalDotEffect,
} from "@phaser/core/effect/HomeEffect";
import { changeLanguage, getCurrentLanguage, t } from "@/i18n/i18n";
import { LanguageBtn } from "../ui/btns/LanguageBtn";

export class HomeScene extends Phaser.Scene {
  private modal!: ModalManager;

  private portalDotEffect?: PortalDotEffect;

  private stageIndex = 0;
  private stagePreview!: Phaser.GameObjects.Image;
  private portal!: Phaser.GameObjects.Image;
  private portalSpin!: Phaser.GameObjects.Image;

  private stageTitle!: Phaser.GameObjects.Text;

  constructor() {
    super("HomeScene");
  }

  async create() {
    const { width, height } = this.scale;

    this.modal = new ModalManager(this);

    AudioManager.playBgm("bgm-home");

    await shopEquipManager.init();

    // 배경
    const bg = this.add.image(width / 2, height / 2, "home-bg");
    bg.setDisplaySize(width, height);

    // 게임 로고
    const logo = this.add.image(width / 2, height * 0.2, "home-logo");
    logo.setOrigin(0.5);

    // 언어 설정 버튼
    new LanguageBtn(this, width - width * 0.42, height * 0.06, {
      label: getCurrentLanguage().toUpperCase(),

      onClick: async () => {
        const next = getCurrentLanguage() === "ko" ? "en" : "ko";

        await changeLanguage(next);

        window.location.reload();
      },
    });

    // 볼륨 버튼
    new VolumeBtn(this, width - width * 0.26, height * 0.06, () => {
      console.log("소리꺼짐");
    });

    // 도움말 버튼
    new HelpBtn(this, width - width * 0.1, height * 0.06, () => {
      this.modal.open(ModalType.HELP);
    });

    // 최고점 표시 패널
    const bestPanel = new BestPanel(this, width * 0.135, height * 0.05);

    const bestScore = this.registry.get("bestScore") ?? 0;
    bestPanel.setBest(bestScore);

    // 중앙 스테이지 프리뷰
    this.stagePreview = this.add
      .image(width / 2, height / 2 + 20, STAGES[this.stageIndex].previewKey)
      .setOrigin(0.5);

    // 포탈 회전 이미지
    this.portalSpin = this.add
      .image(width / 2, height / 2 + 20, "monster-portal-spin-blur")
      .setOrigin(0.5);

    this.tweens.add({
      targets: this.portalSpin,
      angle: 360,
      duration: 6000, // 6초에 한 바퀴
      repeat: -1,
      ease: "Linear",
    });

    // 포탈
    this.portal = this.add
      .image(width / 2, height / 2, "monster-portal")
      .setOrigin(0.5);

    applyFloatingEffect(this, this.portal);

    // 포탈입자 효과
    this.portalDotEffect = new PortalDotEffect(this);

    this.portalDotEffect.start(width / 2, height / 2);

    new SArrowBtn(this, width * 0.1, height / 2, {
      direction: ArrowDirection.LEFT,
      role: ArrowRole.PREV,
      onClick: () => this.changeStage(-1),
    });

    new SArrowBtn(this, width * 0.9, height / 2, {
      direction: ArrowDirection.RIGHT,
      role: ArrowRole.NEXT,
      onClick: () => this.changeStage(+1),
    });

    // 스테이지 제목
    this.stageTitle = this.add
      .text(
        width / 2,
        height / 2 + 270,
        t(`stage.${STAGES[this.stageIndex].id}.name`),
        {
          fontFamily: "Black Han Sans",
          fontSize: "50px",
          color: "#FFFFFF",
          stroke: "#1C120E",
          strokeThickness: 12,
          align: "center",
        },
      )
      .setOrigin(0.5)
      .setDepth(10);

    // START 버튼
    new StartBtn(this, width / 2, height * 0.8, "START", () => {
      const selected = STAGES[this.stageIndex];

      this.scene.start("GameScene", { stageId: selected.id });
    });

    // 하단 네비게이션
    new BottomNavBar(this, width / 2, height, "HOME");

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.portalDotEffect?.stop();
      this.portalDotEffect = undefined;
    });
  }

  private changeStage(dir: 1 | -1) {
    const total = STAGES.length;
    this.stageIndex = (this.stageIndex + dir + total) % total;

    const cur = STAGES[this.stageIndex];

    this.stagePreview.setTexture(cur.previewKey);
    this.stageTitle.setText(t(`stage.${cur.id}.name`));
  }
}
